import React from "react";

// Days and default hours
const defaultOperatingHour = {
  enable: true,
  startTime: "09:00",
  endTime: "17:00",
};
const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const defaultData = {
  name: "",
  area: "",
  color: "",
  address1: "",
  address2: "",
  city: "",
  zipCode: "",
  country: "",
  timezone: "",
  text: "",
  attachmentUrls: [],
  ...Object.fromEntries(
    daysOfWeek.map((day) => [day, { ...defaultOperatingHour }])
  ),
};

// Dynamic Google Map component
const DynamicMap = ({ zipCode }) => {
  const query = zipCode ? encodeURIComponent(zipCode) : "P25 YW68";
  const mapSrc = `https://www.google.com/maps?q=${query}&output=embed`;

  return (
    <div className="w-full max-w-xs h-64 bg-gray-200 rounded-lg overflow-hidden mb-2 flex items-center justify-center">
      <iframe
        title="Dynamic Map"
        src={mapSrc}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
};

// Main EditGeneral component
const EditGeneral = ({ data, setData }) => {
  const safeData = { ...defaultData, ...data };

  // Handle simple field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle nested operating hour changes
  const handleOperatingHourChange = (day, field, value) => {
    setData((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: field === "enable" ? value : value,
      },
    }));
  };

  // Handle file uploads
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const urls = files.map((file) => URL.createObjectURL(file));
    setData((prev) => ({
      ...prev,
      attachmentUrls: [...(prev.attachmentUrls || []), ...urls],
    }));
  };

  // Example submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    // Process data here (e.g., send to API)
    // console.log(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basics */}
      <div className="bg-white px-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Basics</h2>
        <div className="grid grid-cols-2 gap-8">
          <div>
            {/* Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={safeData.name}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            {/* Area */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Area
              </label>
              <input
                type="text"
                name="area"
                value={safeData.area}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            {/* Area Color */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>
              <input
                type="color"
                name="color"
                value={safeData.color}
                onChange={handleChange}
                className="h-10 w-16 p-0 border-gray-300 rounded cursor-pointer"
              />
            </div>
            {/* Address */}
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Address
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={safeData.country}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address line 1
              </label>
              <input
                type="text"
                name="address1"
                value={safeData.address1}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address line 2
              </label>
              <input
                type="text"
                name="address2"
                value={safeData.address2}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Suburb / Town / City
              </label>
              <input
                type="text"
                name="city"
                value={safeData.city}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postal code / ZIP code
              </label>
              <input
                type="text"
                name="zipCode"
                value={safeData.zipCode}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div>
            {/* <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <input
                type="text"
                name="timezone"
                value={safeData.timezone}
                onChange={handleChange}
                className="w-full rounded border-gray-300 bg-slate-100 p-2"
              />
            </div> */}
          </div>
          {/* Dynamic Map */}
          <div className="flex flex-col items-center">
            <DynamicMap zipCode={safeData.zipCode} />
            <button
              type="button"
              className="text-gray-700 text-sm mt-2 hover:underline"
            >
              Adjust pin
            </button>
          </div>
        </div>
      </div>
      {/* Operating Hours */}
      <div className="bg-white p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Operating hours
        </h2>
        <div className="space-y-4">
          {daysOfWeek.map((day) => (
            <div key={day} className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 w-32">
                <input
                  type="checkbox"
                  checked={safeData[day].enable}
                  onChange={(e) =>
                    handleOperatingHourChange(day, "enable", e.target.checked)
                  }
                  className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700 capitalize">
                  {day}
                </label>
              </div>
              <input
                type="time"
                value={safeData[day].startTime}
                onChange={(e) =>
                  handleOperatingHourChange(day, "startTime", e.target.value)
                }
                disabled={!safeData[day].enable}
                className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-gray-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
              />
              <span className="text-gray-500">-</span>
              <input
                type="time"
                value={safeData[day].endTime}
                onChange={(e) =>
                  handleOperatingHourChange(day, "endTime", e.target.value)
                }
                disabled={!safeData[day].enable}
                className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-gray-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>
          ))}
        </div>
      </div>
      {/* Notes */}

      {/* <div className="bg-white p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Notes</h2>
        <textarea
          name="text"
          value={safeData.text}
          onChange={handleChange}
          placeholder="Useful information that will appear in the location notes for every shift."
          className="w-full rounded border-gray-300 bg-slate-100 p-2 h-24"
        />
        <div className="mt-4 flex items-center space-x-4">
          <label className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
            <span className="text-sm text-gray-700">Attachment</span>
            <input
              type="file"
              multiple
              hidden
              onChange={handleFileChange}
              accept="image/jpeg,image/png,application/pdf"
            />
          </label>
          <span className="text-xs text-gray-500">
            Accepted file formats: JPEG, PNG, PDF. Max 5 MB, 10MB per file.
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {safeData.attachmentUrls?.map((url, idx) => (
            <a
              key={idx}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 underline"
            >
              Attachment {idx + 1}
            </a>
          ))}
        </div>
      </div> */}
    </form>
  );
};

export default EditGeneral;
